/**
 * ActivityFeedWidget - Chronological day activity log
 *
 * Shows timestamped events for today: task creates/completions,
 * mood check-ins, day start/end, journal entries, and agent actions.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Sun, Moon, Plus, Check, Heart, BookOpen, Activity, Bot } from 'lucide-react';
import { parseUTCTimestamp } from '../../utils/dateUtils';
import { useDays } from '../../hooks/useDays';
import { useTodos } from '../../hooks/useTodos';
import { useJournal } from '../../hooks/useJournal';
import { useWindows } from '../../contexts/WindowsContext';
import { aiAPI, todosAPI, eventsAPI } from '../../services/api';
import type { AgentAction } from '../../services/api';
import type { ActivityFeedWidgetConfig } from '../../types/widgets';
import { WidgetWrapper } from './WidgetWrapper';
import { ActivityFeedConfigModal } from './ActivityFeedConfigModal';

interface ActivityFeedWidgetProps {
  config: ActivityFeedWidgetConfig;
  widgetId: string;
  windowId: string;
}

interface TimelineEvent {
  time: Date;
  type: string;
  title: string;
  description?: string | null;
  mood?: string | null;
  energy?: string | null;
  icon: React.FC<{ className?: string }>;
  color: string;
  entityType?: 'task' | 'event' | null;
  entityId?: string | null;
  conversationId?: string | null;
}

const TOOL_LABELS: Record<string, string> = {
  create_task: 'AI created task',
  edit_task: 'AI edited task',
  delete_task: 'AI deleted a task',
  complete_habit: 'AI completed habit',
  create_event: 'AI created event',
  edit_event: 'AI edited event',
  delete_event: 'AI deleted an event',
  create_event_from_todo: 'AI scheduled event',
  add_comment: 'AI commented on',
};

function agentActionToEntityType(action: AgentAction): 'task' | 'event' | null {
  if (action.entity_type === 'task' || action.entity_type === 'comment') return 'task';
  if (action.entity_type === 'event') return 'event';
  return null;
}

export function ActivityFeedWidget({ config, widgetId, windowId }: ActivityFeedWidgetProps) {
  const {
    title = 'Day Log',
    showTaskCreations = true,
    showTaskCompletions = true,
    showMoodLogs = true,
    showJournalEntries = true,
    showAgentTaskActions = true,
    showAgentEventActions = true,
    showAgentHabitActions = true,
    showAgentComments = true,
    maxItems,
  } = config;

  const [showConfig, setShowConfig] = useState(false);
  const { updateWidget } = useWindows();

  const today = useMemo(() => new Date(), []);
  const todayStr = useMemo(() => format(today, 'yyyy-MM-dd'), [today]);

  const { currentDay, completedTasks } = useDays(today);
  const { todos } = useTodos({});
  const { entries: journalEntries } = useJournal({
    source: 'daily_general',
  });

  // Fetch agent actions for today
  const { data: agentActions } = useQuery({
    queryKey: ['agent-activity', todayStr],
    queryFn: () => aiAPI.getActivity(todayStr),
    staleTime: 60_000,
  });

  const handleEntityClick = useCallback(async (entityType: 'task' | 'event', entityId: string) => {
    try {
      if (entityType === 'task') {
        const todo = await todosAPI.get(entityId);
        window.dispatchEvent(new CustomEvent('openTaskFromWidget', { detail: { todo } }));
      } else {
        const event = await eventsAPI.get(entityId);
        window.dispatchEvent(new CustomEvent('openEventFromWidget', { detail: { event } }));
      }
    } catch {
      // Entity may have been deleted
    }
  }, []);

  const handleConversationClick = useCallback((conversationId: string) => {
    window.dispatchEvent(new CustomEvent('openAIConversation', { detail: { conversationId } }));
  }, []);

  const timeline = useMemo(() => {
    const events: TimelineEvent[] = [];

    // Day started
    if (currentDay?.is_day_started && currentDay.day_started_time) {
      events.push({
        time: parseUTCTimestamp(currentDay.day_started_time),
        type: 'day_start',
        title: 'Day Started',
        mood: currentDay.morning_mood,
        energy: currentDay.morning_energy,
        icon: Sun,
        color: 'text-yellow-500',
      });
    }

    // Task creations
    if (showTaskCreations && todos) {
      todos.forEach((task) => {
        if (task.created_time) {
          const createdDate = parseUTCTimestamp(task.created_time);
          if (format(createdDate, 'yyyy-MM-dd') === todayStr) {
            events.push({
              time: createdDate,
              type: 'task_created',
              title: `Created: ${task.title || 'Untitled'}`,
              description: task.description,
              icon: Plus,
              color: 'text-blue-500',
              entityType: 'task',
              entityId: task.id,
            });
          }
        }
      });
    }

    // Task completions
    if (showTaskCompletions && completedTasks) {
      completedTasks.forEach((task) => {
        if (task.completed_date) {
          events.push({
            time: parseUTCTimestamp(task.completed_date),
            type: 'task_completed',
            title: `Completed: ${task.title || 'Untitled'}`,
            description: task.description,
            icon: Check,
            color: 'text-green-500',
            entityType: 'task',
            entityId: task.id,
          });
        }
      });
    }

    // Mood logs
    if (showMoodLogs && currentDay?.mood_logs) {
      currentDay.mood_logs.forEach((entry) => {
        if (entry.timestamp) {
          events.push({
            time: parseUTCTimestamp(entry.timestamp),
            type: 'mood_log',
            title: 'Mood & Energy Check-in',
            mood: entry.mood,
            energy: entry.energy,
            icon: Heart,
            color: 'text-pink-500',
          });
        }
      });
    }

    // Journal entries
    if (showJournalEntries && journalEntries) {
      journalEntries.forEach((entry) => {
        if (entry.created_time) {
          const createdDate = parseUTCTimestamp(entry.created_time);
          if (format(createdDate, 'yyyy-MM-dd') === todayStr) {
            events.push({
              time: createdDate,
              type: 'journal_entry',
              title: entry.prompt || 'Journal Entry',
              description: entry.content,
              mood: entry.mood,
              energy: entry.energy,
              icon: BookOpen,
              color: 'text-amber-500',
            });
          }
        }
      });
    }

    // Agent actions
    if (agentActions) {
      agentActions.forEach((action: AgentAction) => {
        // Filter by category toggles
        const et = action.entity_type;
        if (et === 'task' && !showAgentTaskActions) return;
        if (et === 'event' && !showAgentEventActions) return;
        if (et === 'habit' && !showAgentHabitActions) return;
        if (et === 'comment' && !showAgentComments) return;

        const label = TOOL_LABELS[action.tool_name] || action.tool_name;
        const titleText = action.entity_title
          ? `${label}: ${action.entity_title}`
          : label;

        events.push({
          time: action.created_time ? parseUTCTimestamp(action.created_time) : new Date(),
          type: 'agent_action',
          title: titleText,
          icon: Bot,
          color: 'text-indigo-500',
          entityType: agentActionToEntityType(action),
          entityId: action.entity_id,
          conversationId: action.conversation_id,
        });
      });
    }

    // Day ended
    if (currentDay?.is_day_ended && currentDay.day_ended_time) {
      events.push({
        time: parseUTCTimestamp(currentDay.day_ended_time),
        type: 'day_end',
        title: 'Day Ended',
        mood: currentDay.evening_mood,
        energy: currentDay.evening_energy,
        icon: Moon,
        color: 'text-purple-500',
      });
    }

    // Sort chronologically
    events.sort((a, b) => a.time.getTime() - b.time.getTime());

    if (maxItems && maxItems > 0) {
      return events.slice(0, maxItems);
    }
    return events;
  }, [
    currentDay,
    completedTasks,
    todos,
    journalEntries,
    agentActions,
    todayStr,
    showTaskCreations,
    showTaskCompletions,
    showMoodLogs,
    showJournalEntries,
    showAgentTaskActions,
    showAgentEventActions,
    showAgentHabitActions,
    showAgentComments,
    maxItems,
  ]);

  return (
    <WidgetWrapper
      title={title}
      widgetId={widgetId}
      windowId={windowId}
      onSettings={() => setShowConfig(true)}
    >
      <div className="h-full flex flex-col p-3">
        <div className="flex items-center gap-2 mb-3 px-1">
          <Activity className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
            {timeline.length} events
          </span>
        </div>

        <div className="flex-1 overflow-auto min-h-0">
          {timeline.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500 py-8">
              <Activity className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm">No activity yet today</p>
            </div>
          ) : (
            <div className="space-y-2">
              {timeline.map((event, index) => {
                const Icon = event.icon;
                const isClickable = !!(event.entityType && event.entityId);
                const isAgent = event.type === 'agent_action';

                return (
                  <div key={`${event.type}-${index}`} className="flex items-start gap-2.5 px-1">
                    <div className="flex-shrink-0 mt-0.5">
                      <Icon className={`w-4 h-4 ${event.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {isClickable ? (
                            <button
                              onClick={() => handleEntityClick(event.entityType!, event.entityId!)}
                              className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate hover:underline text-left"
                            >
                              {event.title}
                            </button>
                          ) : (
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                              {event.title}
                            </p>
                          )}
                          {isAgent && event.conversationId && (
                            <button
                              onClick={() => handleConversationClick(event.conversationId!)}
                              className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400 rounded hover:bg-indigo-200 dark:hover:bg-indigo-800/50 transition-colors"
                            >
                              via AI
                            </button>
                          )}
                        </div>
                        <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                          {format(event.time, 'h:mm a')}
                        </span>
                      </div>
                      {event.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                          {event.description}
                        </p>
                      )}
                      {(event.mood || event.energy) && (
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                          {event.mood && <span>Mood: {event.mood}</span>}
                          {event.energy && <span>Energy: {event.energy}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showConfig && (
        <ActivityFeedConfigModal
          config={config}
          onSave={(newConfig) => {
            updateWidget(windowId, widgetId, newConfig);
            setShowConfig(false);
          }}
          onClose={() => setShowConfig(false)}
        />
      )}
    </WidgetWrapper>
  );
}
